import {
  AfterViewInit,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
} from '@angular/core';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { LotsService } from 'src/app/services/lots.service';
import { UserState } from 'src/app/store/user/user.reducer';
import { Observable, combineLatest, map, of, switchMap } from 'rxjs';
import { Lot, TableLotData } from '../lots.model';
import { UserDetails, UserRoles } from '../../login/user.model';
import { Router, RouterModule } from '@angular/router';
import { selectLot } from 'src/app/store/lots-library/lot.actions';
import { NegotiationDetailsCardComponent } from './negotiation-details-card/negotiation-details-card.component';
import { LotDetailsCardComponent } from './lot-details-card/lot-details-card.component';
import { TableDataState } from 'src/app/store/table-data/table-data.reducer';
import {
  changeTableMarket,
  fetchData,
} from 'src/app/store/table-data/table-data.actions';
import { TradesViewerComponent } from './trades-viewer/trades-viewer.component';
import { DomesticPipe } from 'src/app/pipes/domestic-city.pipe';
import { SameYearPipe } from 'src/app/pipes/same-year.pipe';
import { MarketType } from '../lots.model';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { PriceTypePipe } from 'src/app/pipes/price-type.pipe';
import { BlockUserRolesDirective } from 'src/app/directives/block-user-roles.directive';
import { faHandshake } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  selectTableData,
  selectTableSelectedMarket,
} from 'src/app/store/table-data/table-data.selectors';
import { selectUser } from 'src/app/store/user/user.selectors';
import { JCTradingIndicationComponent } from '../../custom/chips/jc-trading-indication/jc-trading-indication.component';

@Component({
  selector: 'app-desktop-view',
  imports: [
    CommonModule,
    SameYearPipe,
    DomesticPipe,
    TradesViewerComponent,
    PriceTypePipe,
    RouterModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatTableModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    LotDetailsCardComponent,
    NegotiationDetailsCardComponent,
    MatTooltipModule,
    BlockUserRolesDirective,
    FontAwesomeModule,
    MatTooltipModule,
    JCTradingIndicationComponent,
  ],
  templateUrl: './desktop-view.component.html',
  styleUrl: './desktop-view.component.css',
  standalone: true,
})
export class DesktopViewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('counter') counter: NegotiationDetailsCardComponent;
  userRoles = UserRoles;
  marketTypes = MarketType;
  displayColumns: string[] = [
    'lot_type',
    'commodity',
    'price',
    'volume_value',
    'delivery_type',
    'ship_begin', // labelled with beign for sorting purposes but contains both begin and end shipping dates
  ];
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource: MatTableDataSource<TableLotData>;
  activeLotId = signal<string>('');
  lots$: Observable<Lot[]>;
  searchValue: string = '';
  selectedMarket: MarketType = null;
  faHandShake = faHandshake;

  userDetails$: Observable<UserDetails>;
  constructor(
    private destroyRef: DestroyRef,
    private store: Store<{
      user: UserState;
      lot: Lot;
      lotTableData: TableDataState;
    }>,
    private router: Router,
    private lotsService: LotsService
  ) {}

  ngOnInit(): void {
    /** get app state user data and default market;
    check first if table data selected market is already selected;
    if not, get user default market and get lots for that market;
  */
    this.userDetails$ = combineLatest([
      this.store.select(selectUser),
      this.store.select(selectTableSelectedMarket),
    ]).pipe(
      map(([user, market]) => {
        if (market !== null) {
          this.selectedMarket = market;
        }
        if (user) {
          if (this.selectedMarket === null) {
            this.selectedMarket = user.default_market_type;
          }
          this.getLots(this.selectedMarket);
        }
        return user;
      }),
      takeUntilDestroyed(this.destroyRef)
    );

    this.store
      .select(selectTableData)
      .pipe(
        map((res) => {
          const negotiationLotIds = new Set(
            res.negotiations.map((n) => n.lot_id)
          );
          const tableLotData = res.lots.map((lot) => ({
            ...lot,
            hasNegotiations: negotiationLotIds.has(lot._id),
          }));
          this.dataSource = new MatTableDataSource(tableLotData);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          // this.paginator.pageSize = 20;
          if (res.lots.length) {
            this.store.dispatch(selectLot({ lotDetails: res.lots[0] })); // selects the first lot to display details
            this.activeLotId.set(res.lots[0]._id);
          } else {
            this.store.dispatch(
              selectLot({ lotDetails: { _id: 'none selected' } as Lot })
            );
            this.activeLotId.set(null);
            return res.selectedMarket;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  getLots(market: MarketType) {
    // console.log('called getLots', market)
    this.store.dispatch(
      fetchData({ limit: 40, marketType: MarketType[market] })
    );
    this.store.dispatch(changeTableMarket({ marketType: MarketType[market] }));
    return;
  }

  ngAfterViewInit() {}

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  clickRow(selectedLot: Lot): void {
    // this.router.navigate(['/lots', lotId]);
    this.store.dispatch(selectLot({ lotDetails: selectedLot }));
    this.activeLotId.set(selectedLot._id);
  }

  navigateToNew(page: string): void {
    this.router.navigate([`app/lots/new/${page}`]);
  }

  scrollToCounter(): void {
    // this.counter.nativeElement.scrollIntoView({ behavior: 'smooth' });
    this.counter.focusOnInput();
  }

  ngOnDestroy(): void {}
}
